import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import MessageContextMenu from './MessageContextMenu';
import ViewOnceMedia from './ViewOnceMedia';
import ForwardPicker from './ForwardPicker';
import VoiceMessage from './VoiceMessage';
import LocationMessage from './LocationMessage';
import ContactMessage from './ContactMessage';
import config from '../config';
import { getImageUrl } from '../utils/imageUtils';
import { renderTextWithLinks } from '../utils/linkify';
import '../styles/LocationMessage.css';
import '../styles/ContactMessage.css';

const GroupMessages = ({ group, user, socket, currentUserLocation, onReply, onContactClick, searchTerm = '', clearedAt, onEdit, onImageClick, pinnedMessageIds = [], canPin = false, onPin }) => {
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const isInitialLoadRef = useRef(true); // Track if this is the initial load
  const [contextMenu, setContextMenu] = useState({
    isVisible: false,
    position: { x: 0, y: 0 },
    message: null
  });
  const [reactionDetailsMessageId, setReactionDetailsMessageId] = useState(null);
  const [forwardModal, setForwardModal] = useState({
    isVisible: false,
    message: null,
  });

  // Pagination: load newest messages first, then fetch older ones on scroll-up
  const PAGE_SIZE = 50;
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  // When true, the scroll-to-bottom effect is skipped (we just prepended older messages)
  const isPrependingRef = useRef(false);

  useEffect(() => {
    if (group && socket) {
      // Reset initial load flag when group changes
      isInitialLoadRef.current = true;
      setReactionDetailsMessageId(null);
      fetchGroupMessages();
      const cleanup = setupSocketListeners();
      
      // Return cleanup function to remove listeners when group changes
      return () => {
        if (cleanup) cleanup();
      };
    }
  }, [group, socket]);

  const setupSocketListeners = () => {
    // Store current group ID to prevent cross-group message leakage
    const currentGroupId = group._id;
    
    // Handler for receiveGroupMessage
    const handleReceiveGroupMessage = (message) => {
      // Double-check: Only process messages for the current group
      // This prevents messages from other groups from appearing if socket room logic fails
      if (message.groupId === currentGroupId && message.groupId === group._id) {
        // Check if message already exists to avoid duplicates
        setMessages(prev => {
          const exists = prev.some(m => m._id === message._id);
          if (!exists) {
            return [...prev, message];
          }
          return prev;
        });
      }
    };

    // Handler for newGroupMessage (including system messages)
    const handleNewGroupMessage = (message) => {
      // Double-check: Only process messages for the current group
      // This prevents messages from other groups from appearing if socket room logic fails
      if (message.groupId === currentGroupId && message.groupId === group._id) {
        // Check if message already exists to avoid duplicates
        setMessages(prev => {
          const exists = prev.some(m => m._id === message._id);
          if (!exists) {
            // If this is a message from someone else, mark it as read immediately
            // since the user is currently viewing this group
            if (message.from && message.from._id !== user.id) {
              markSingleMessageAsRead(message._id);
            }
            return [...prev, message];
          }
          return prev;
        });
      }
    };

    // Handler for group message errors
    const handleGroupMessageError = (error) => {
      setError(error.message);
      setTimeout(() => setError(''), 5000);
    };

    // Handler for group message deletion
    const handleGroupMessageDeleted = (data) => {
      const { messageId, groupId: deletedGroupId, deleteType, updated } = data;
      
      // Only process if it's for the current group
      if (deletedGroupId === currentGroupId && deletedGroupId === group._id) {
        if (deleteType === 'forEveryone') {
          // Update message to show deleted state
          setMessages(prev => prev.map(msg => 
            msg._id === messageId
              ? {
                  ...msg,
                  messageType: 'deleted',
                  text: updated?.text || 'This message was deleted',
                  deletedForEveryone: true,
                  deletedBy: updated?.deletedBy,
                  deletedAt: updated?.deletedAt
                }
              : msg
          ));
        } else if (deleteType === 'forMe') {
          // Remove message from view for this user
          setMessages(prev => prev.filter(msg => msg._id !== messageId));
        }
      }
    };

    // Handler for group message edit
    const handleGroupMessageEdited = (data) => {
      const { messageId, groupId: editedGroupId, text, isEdited, editedAt, updatedMessage } = data;
      
      // Only process if it's for the current group
      if (editedGroupId === currentGroupId && editedGroupId === group._id) {
        setMessages(prev => prev.map(msg => 
          msg._id === messageId
            ? {
                ...msg,
                text: text,
                isEdited: isEdited,
                editedAt: editedAt
              }
            : msg
        ));
      }
    };

    // Handler for live reaction add/remove
    const handleGroupMessageReactionUpdated = (payload) => {
      const {
        messageId,
        groupId: reactionGroupId,
        reactions,
      } = payload || {};

      if (!messageId) return;
      if (
        String(reactionGroupId) !== String(currentGroupId) ||
        String(reactionGroupId) !== String(group._id)
      ) {
        return;
      }

      setMessages((prev) =>
        prev.map((msg) =>
          String(msg._id) === String(messageId)
            ? { ...msg, reactions: Array.isArray(reactions) ? reactions : [] }
            : msg
        )
      );
    };

    // Register listeners
    socket.on('receiveGroupMessage', handleReceiveGroupMessage);
    socket.on('newGroupMessage', handleNewGroupMessage);
    socket.on('groupMessageError', handleGroupMessageError);
    socket.on('groupMessageDeleted', handleGroupMessageDeleted);
    socket.on('groupMessageEdited', handleGroupMessageEdited);
    socket.on('groupMessageReactionUpdated', handleGroupMessageReactionUpdated);

    const handleGroupMessageViewOnceUpdated = (data) => {
      if (!data?.messageId) return;
      if (String(data.groupId) !== String(currentGroupId)) return;
      setMessages((prev) =>
        prev.map((msg) =>
          String(msg._id) === String(data.messageId)
            ? {
                ...msg,
                viewedBy: data.viewedBy || msg.viewedBy || [],
                viewOnceViewed:
                  String(data.openedBy) === String(user.id) || msg.viewOnceViewed,
              }
            : msg
        )
      );
    };
    socket.on('groupMessageViewOnceUpdated', handleGroupMessageViewOnceUpdated);

    // Return cleanup function
    return () => {
      socket.off('receiveGroupMessage', handleReceiveGroupMessage);
      socket.off('newGroupMessage', handleNewGroupMessage);
      socket.off('groupMessageError', handleGroupMessageError);
      socket.off('groupMessageDeleted', handleGroupMessageDeleted);
      socket.off('groupMessageEdited', handleGroupMessageEdited);
      socket.off('groupMessageReactionUpdated', handleGroupMessageReactionUpdated);
      socket.off('groupMessageViewOnceUpdated', handleGroupMessageViewOnceUpdated);
    };
  };

  // Function to mark a single message as read
  const markSingleMessageAsRead = async (messageId) => {
    try {
      await axios.post(`${config.apiUrl}/api/groups/message/${messageId}/mark-read`, {
        userId: user.id
      });
      //console.log(`Marked message ${messageId} as read`);
    } catch (error) {
      console.error('Error marking single message as read:', error);
    }
  };

  const fetchGroupMessages = async () => {
    try {
      setIsLoading(true);
      setError('');
      // Reset initial load flag when fetching new group messages
      isInitialLoadRef.current = true;
      // Reset pagination for the newly opened group
      setPage(1);
      setHasMore(true);
      isPrependingRef.current = false;
      const response = await axios.get(`${config.apiUrl}/api/groups/${group._id}/messages`, {
        params: { userId: user?.id, page: 1, limit: PAGE_SIZE }
      });
      const list = Array.isArray(response.data) ? response.data : [];
      setMessages(list);
      // If we received fewer than a full page, there are no older messages
      setHasMore(list.length >= PAGE_SIZE);
    } catch (error) {
      console.error('Error fetching group messages:', error);
      setError('Failed to fetch group messages');
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch older messages (next page) and prepend them, preserving scroll position
  const loadOlderMessages = async () => {
    if (isLoadingMore || !hasMore || isInitialLoadRef.current) return;

    const scrollContainer = messagesContainerRef.current?.closest('.chat-window');
    const prevScrollHeight = scrollContainer ? scrollContainer.scrollHeight : 0;
    const prevScrollTop = scrollContainer ? scrollContainer.scrollTop : 0;

    try {
      setIsLoadingMore(true);
      isPrependingRef.current = true;
      const nextPage = page + 1;
      const response = await axios.get(`${config.apiUrl}/api/groups/${group._id}/messages`, {
        params: { userId: user?.id, page: nextPage, limit: PAGE_SIZE }
      });
      const older = Array.isArray(response.data) ? response.data : [];

      if (older.length < PAGE_SIZE) {
        setHasMore(false);
      }

      if (older.length > 0) {
        setMessages((prev) => {
          const existingIds = new Set(prev.map((m) => m._id));
          const deduped = older.filter((m) => !existingIds.has(m._id));
          return [...deduped, ...prev];
        });
        setPage(nextPage);

        // Restore scroll position so the view stays anchored to the same message
        requestAnimationFrame(() => {
          if (scrollContainer) {
            const newScrollHeight = scrollContainer.scrollHeight;
            scrollContainer.scrollTop =
              prevScrollTop + (newScrollHeight - prevScrollHeight);
          }
        });
      }
    } catch (error) {
      console.error('Error loading older group messages:', error);
    } finally {
      setIsLoadingMore(false);
      // Release the prepend guard after the DOM settles so future new messages
      // still auto-scroll to the bottom
      setTimeout(() => {
        isPrependingRef.current = false;
      }, 80);
    }
  };

  const scrollToBottom = () => {
    // Scroll only within the chat window container (not the page)
    const chatWindow = messagesEndRef.current?.closest('.chat-window');
    if (!chatWindow) return;

    const apply = () => {
      // behavior:'auto' overrides CSS scroll-behavior:smooth so we don't undershoot
      chatWindow.scrollTo({ top: chatWindow.scrollHeight, behavior: 'auto' });
    };

    apply();
    requestAnimationFrame(() => {
      apply();
      requestAnimationFrame(apply);
    });
  };

  // Load older messages when the user scrolls near the top of the chat window
  useEffect(() => {
    const scrollContainer = messagesContainerRef.current?.closest('.chat-window');
    if (!scrollContainer) return;

    const handleScroll = () => {
      if (
        scrollContainer.scrollTop <= 80 &&
        hasMore &&
        !isLoadingMore &&
        !isInitialLoadRef.current
      ) {
        loadOlderMessages();
      }
    };

    scrollContainer.addEventListener('scroll', handleScroll);
    return () => scrollContainer.removeEventListener('scroll', handleScroll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMore, isLoadingMore, page, group?._id, isLoading]);

  // Scroll to latest message when messages load/update (retries for layout)
  useEffect(() => {
    if (!messages.length) return;
    // Skip auto-scroll when we just prepended older messages (keep anchor)
    if (isPrependingRef.current) return;

    const delays = isInitialLoadRef.current ? [0, 50, 150, 350] : [50];
    const timers = delays.map((ms, idx) =>
      setTimeout(() => {
        scrollToBottom();
        if (isInitialLoadRef.current && idx === delays.length - 1) {
          isInitialLoadRef.current = false;
        }
      }, ms)
    );
    return () => timers.forEach(clearTimeout);
  }, [messages]);

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Format a group call duration (seconds) as h:mm:ss / m:ss for the call log.
  const formatCallDuration = (totalSeconds) => {
    const secs = Math.max(0, parseInt(totalSeconds, 10) || 0);
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    const s = secs % 60;
    if (h > 0) {
      return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    }
    return `${m}:${String(s).padStart(2, '0')}`;
  };

  const getDateLabel = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = now.setHours(0,0,0,0) - date.setHours(0,0,0,0);
    const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7 && diffDays > 1) {
      return date.toLocaleDateString(undefined, { weekday: 'long' });
    }
    return date.toLocaleDateString();
  };

  // Context menu handlers
  const handleMessageContextMenu = (event, message) => {
    event.preventDefault();
    event.stopPropagation();
    
    // //console.log('Group message context menu triggered:', {
    //   messageId: message._id,
    //   message: message,
    //   hasId: !!message._id
    // });
    
    const isMobile = window.innerWidth <= 768;
    
    let position;
    if (isMobile) {
      // On mobile, show at bottom
      position = { x: 0, y: window.innerHeight - 300 };
    } else {
      // On desktop, show near the click position
      position = { 
        x: Math.min(event.clientX, window.innerWidth - 250),
        y: Math.min(event.clientY, window.innerHeight - 300)
      };
    }
    
    setContextMenu({
      isVisible: true,
      position,
      message
    });
  };

  const closeContextMenu = () => {
    setContextMenu({
      isVisible: false,
      position: { x: 0, y: 0 },
      message: null
    });
  };

  const handleDeleteMessage = async (deleteType) => {
    try {
      if (!contextMenu.message || !contextMenu.message._id) {
        console.error('No group message selected for deletion');
        alert('No message selected for deletion');
        return;
      }

      // //console.log('Attempting to delete group message:', {
      //   messageId: contextMenu.message._id,
      //   deleteType,
      //   userId: user.id
      // });

      // For group messages, we need to use a different API endpoint
      const response = await axios.delete(`${config.apiUrl}/api/groups/messages/${contextMenu.message._id}`, {
        data: {
          deleteType,
          userId: user.id,
          groupId: group._id
        }
      });
      
      //console.log('Group message delete response:', response.data);
      
      if (deleteType === 'forMe') {
        // For "Delete for me", only remove from local state
        // Socket event will also update it, but this provides immediate feedback
        setMessages(prev => prev.filter(msg => msg._id !== contextMenu.message._id));
      } else {
        // For "Delete for everyone", update message immediately
        // Socket event will also update it for all users, but this provides immediate feedback
        setMessages(prev => prev.map(msg => 
          msg._id === contextMenu.message._id
            ? {
                ...msg,
                messageType: 'deleted',
                text: 'This message was deleted',
                deletedForEveryone: true,
                deletedBy: user.id,
                deletedAt: new Date()
              }
            : msg
        ));
      }
      
      closeContextMenu();
    } catch (error) {
      console.error('Error deleting group message:', error);
      alert(`Failed to delete message. Error: ${error.response?.data?.error || error.message}`);
    }
  };

  const handlePinMessage = async (messageId, isPinned) => {
    if (onPin) {
      await onPin(messageId, isPinned);
      return;
    }
    try {
      await axios.put(`${config.apiUrl}/api/groups/messages/${messageId}/pin`, {
        isPinned,
        userId: user.id,
        groupId: group._id
      });
    } catch (error) {
      console.error('Error pinning group message:', error);
      alert(`Failed to ${isPinned ? 'pin' : 'unpin'} message. Error: ${error.response?.data?.error || error.message}`);
    }
  };

  const handleStarMessage = async (messageId, isStarred) => {
    try {
      //console.log('Attempting to star group message:', { messageId, isStarred });
      
      const response = await axios.put(`${config.apiUrl}/api/groups/messages/${messageId}/star`, {
        isStarred,
        userId: user.id,
        groupId: group._id
      });
      
      //console.log('Star group message response:', response.data);
      
      // Update message in local state
      setMessages(prev => 
        prev.map(msg => {
          if (msg._id === messageId) {
            const starredBy = msg.starredBy || [];
            if (isStarred) {
              // Add user to starredBy array if not already there
              if (!starredBy.includes(user.id)) {
                return { ...msg, starredBy: [...starredBy, user.id] };
              }
            } else {
              // Remove user from starredBy array
              return { ...msg, starredBy: starredBy.filter(id => id !== user.id) };
            }
          }
          return msg;
        })
      );
      
    } catch (error) {
      console.error('Error starring group message:', error);
      alert(`Failed to star message. Error: ${error.response?.data?.error || error.message}`);
    }
  };

  const handleReplyToMessage = (message) => {
    //console.log('Reply to group message:', message);
    if (onReply) {
      onReply(message);
    }
    closeContextMenu();
  };

  const handleEditMessage = (message) => {
    if (onEdit) {
      onEdit(message);
    }
    closeContextMenu();
  };

  // Open the forward picker for the selected group message.
  const handleForwardMessage = (message) => {
    setForwardModal({ isVisible: true, message });
    closeContextMenu();
  };

  const closeForwardModal = () => {
    setForwardModal({ isVisible: false, message: null });
  };

  // Forward the selected group message to the chosen users/groups.
  const handleForwardConfirm = async (targets) => {
    const message = forwardModal.message;
    if (!message || !message._id || !Array.isArray(targets) || targets.length === 0) {
      return;
    }
    try {
      await axios.post(`${config.apiUrl}/api/messages/forward`, {
        from: user.id,
        messageIds: [message._id],
        sourceType: 'group',
        targets: targets.map((t) => ({ type: t.type, id: t.id })),
      });
    } catch (error) {
      console.error('Error forwarding group message:', error);
      alert('Failed to forward message. Please try again.');
    } finally {
      closeForwardModal();
    }
  };

  const handleViewMessageStatus = async (message) => {
    if (!message?._id) return null;

    const formatParticipant = (participant) => {
      if (!participant) return "N/A";
      if (typeof participant === "string") return participant;
      if (typeof participant === "object") {
        return (
          participant.username ||
          participant.name ||
          participant.phoneNumber ||
          participant.phone ||
          participant._id ||
          participant.id ||
          "Unknown"
        );
      }
      return String(participant);
    };

    try {
      const response = await axios.get(
        `${config.apiUrl}/api/groups/messages/${message._id}/status`,
        {
          params: { userId: user.id },
        }
      );

      const data = response.data || {};
      const readBy = Array.isArray(data.readBy) ? data.readBy : [];

      return {
        id: message._id,
        timestamp: new Date(message.createdAt).toLocaleString(),
        status: data.status || (readBy.length > 0 ? "read" : "sent"),
        messageType: message.messageType || "text",
        from: formatParticipant(message.from),
        to: group?.name || "Group",
        text:
          message.messageType === "voice"
            ? "Voice message"
            : message.text || "(No message text)",
        readBy,
        readCount: data.readCount ?? readBy.length,
        recipientCount: data.recipientCount ?? 0,
      };
    } catch (error) {
      console.error("Error fetching group message status:", error);
      alert(error?.response?.data?.error || "Failed to fetch message status");
      return null;
    }
  };

  // React to a group message (pass falsy type to remove own reaction)
  const handleReactToMessage = async (message, type) => {
    try {
      const currentUserId = user?.id?.toString?.() || '';

      // Optimistic UI update
      setMessages(prev => 
        prev.map(m => {
          if (m._id !== message._id) return m;
          const reactions = (m.reactions || []).filter((r) => {
            const reactionUserId = (
              r?.userId?._id ||
              r?.userId?.id ||
              r?.userId
            )?.toString?.();
            return reactionUserId !== currentUserId;
          });
          if (type) reactions.push({ userId: user.id, type, reactedAt: new Date().toISOString() });
          return { ...m, reactions };
        })
      );

      // Persist to server
      await axios.put(`${config.apiUrl}/api/groups/messages/${message._id}/reaction`, {
        userId: user.id,
        type: type || null,
        groupId: group._id
      });

      // Emit socket for realtime sync
      socket.emit("groupMessageReaction", {
        messageId: message._id,
        userId: user.id,
        type: type || null,
        groupId: group._id,
      });

    } catch (error) {
      console.error('Error reacting to group message:', error);
    }
  };

  const openReactionDetails = (message) => {
    if (!message?.reactions?.length) return;
    setReactionDetailsMessageId(message._id);
  };

  const closeReactionDetails = () => {
    setReactionDetailsMessageId(null);
  };

  const handleRemoveOwnReaction = (message) => {
    handleReactToMessage(message, null);
    closeReactionDetails();
  };

  const getReactionUserId = (reaction) => {
    return (
      reaction?.userId?._id ||
      reaction?.userId?.id ||
      reaction?.userId
    )?.toString?.() || '';
  };

  const getDisplayNameFromUserObj = (userObj) => {
    if (!userObj || typeof userObj !== 'object') return '';
    return userObj.username || userObj.name || userObj.phoneNumber || userObj.phone || '';
  };

  const getReactionUserDisplayName = (reaction) => {
    const reactionUserId = getReactionUserId(reaction);
    if (!reactionUserId) return 'Unknown user';
    if (reactionUserId === user.id?.toString?.()) return 'You';

    const directName = getDisplayNameFromUserObj(reaction?.userId);
    if (directName) return directName;

    if (Array.isArray(group?.members)) {
      const matchedMember = group.members.find((member) => {
        const memberUser = member?.user || member;
        const memberId = (memberUser?._id || memberUser?.id || memberUser)?.toString?.();
        return memberId === reactionUserId;
      });
      if (matchedMember) {
        const memberUser = matchedMember?.user || matchedMember;
        const memberName = getDisplayNameFromUserObj(memberUser);
        if (memberName) return memberName;
      }
    }

    const matchedSender = messages.find((m) => m?.from?._id?.toString?.() === reactionUserId)?.from;
    if (matchedSender?.username) return matchedSender.username;

    return 'Unknown user';
  };

  // Group messages by date
  // Apply filters: cleared timestamp and search term
  // IMPORTANT: Also filter by groupId to prevent cross-group message leakage
  const filteredMessages = messages.filter((msg) => {
    // Safety check: Only show messages belonging to the current group
    if (msg.groupId && msg.groupId !== group._id) {
      return false;
    }
    
    if (clearedAt) {
      const created = new Date(msg.createdAt).getTime();
      if (created <= clearedAt) return false;
    }
    const term = (searchTerm || '').trim().toLowerCase();
    if (!term) return true;
    // Search over text and media names
    if (msg.text && msg.text.toLowerCase().includes(term)) return true;
    if (msg.imageUrl && msg.imageUrl.toLowerCase().includes(term)) return true;
    if (msg.videoUrl && msg.videoUrl.toLowerCase().includes(term)) return true;
    if (msg.audioName && msg.audioName.toLowerCase().includes(term)) return true;
    if (msg.documentName && msg.documentName.toLowerCase().includes(term)) return true;
    if (msg.contactData && msg.contactData.username && msg.contactData.username.toLowerCase().includes(term)) return true;
    if (msg.locationData && msg.locationData.address && msg.locationData.address.toLowerCase().includes(term)) return true;
    return false;
  });

  const groupedMessages = {};
  filteredMessages.forEach(msg => {
    const label = getDateLabel(msg.createdAt);
    if (!groupedMessages[label]) groupedMessages[label] = [];
    groupedMessages[label].push(msg);
  });
  const reactionDetailsMessage = reactionDetailsMessageId
    ? messages.find((m) => m._id === reactionDetailsMessageId)
    : null;
  const dateLabels = Object.keys(groupedMessages);

  if (isLoading) {
    return (
      <div className="group-messages">
        <div className="group-welcome">
          <div className="loading-spinner"></div>
          <p>Loading group messages...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="group-messages">
        <div className="error-message">
          {error}
          <button onClick={fetchGroupMessages} className="retry-button">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="group-messages" ref={messagesContainerRef}>
        {messages.length === 0 ? (
          <div className="group-welcome">
            <h3>Welcome to {group.name}!</h3>
            <p>Start chatting with your group members</p>
          </div>
        ) : (
          <>
            {isLoadingMore && (
              <div
                className="group-loading-more"
                style={{ textAlign: 'center', padding: '8px', fontSize: '12px', opacity: 0.7 }}
              >
                Loading older messages...
              </div>
            )}
            {dateLabels.map(label => (
              <React.Fragment key={label}>
                <div className="date-divider">{label}</div>
                {groupedMessages[label].map((msg, i) => {
                  const isMe = msg.from._id === user.id;
                  const isSystemMessage = msg.messageType === 'system';
                  const isDeletedMessage = msg.messageType === 'deleted' || msg.deletedForEveryone;
                  
                  // Render system messages differently (WhatsApp style - centered)
                  if (isSystemMessage) {
                    return (
                      <div key={msg._id || i} className="system-message">
                        <div className="system-message-content">
                          <span className="system-message-text">{msg.text}</span>
                          <span className="system-message-time">{formatTime(msg.createdAt)}</span>
                        </div>
                      </div>
                    );
                  }

                  // Render group call-history entries (WhatsApp style - centered).
                  if (msg.messageType === 'call') {
                    const cd = msg.callData || {};
                    const isVideoCall = cd.callType === 'video';
                    const answered = cd.callStatus === 'answered';
                    const initiatorName = isMe ? 'You' : (msg.from?.username || 'Someone');
                    const subtitle = answered
                      ? formatCallDuration(cd.duration)
                      : 'No answer';
                    return (
                      <div
                        key={msg._id || i}
                        className="group-call-log"
                        onDoubleClick={() => handleReplyToMessage(msg)}
                        onContextMenu={(e) => handleMessageContextMenu(e, msg)}
                        onTouchStart={(e) => {
                          const timer = setTimeout(() => {
                            handleMessageContextMenu(e, msg);
                          }, 500);
                          const handleTouchEnd = () => {
                            clearTimeout(timer);
                            document.removeEventListener('touchend', handleTouchEnd);
                          };
                          document.addEventListener('touchend', handleTouchEnd);
                        }}
                      >
                        <div className={`group-call-log-bubble ${answered ? 'call-answered' : 'call-missed'}`}>
                          <span className="group-call-log-icon">
                            {isVideoCall ? (
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M23 7l-7 5 7 5V7z" />
                                <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
                              </svg>
                            ) : (
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.36 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.34 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
                              </svg>
                            )}
                          </span>
                          <div className="group-call-log-info">
                            <span className="group-call-log-title">
                              {isVideoCall ? 'Video call' : 'Voice call'}
                            </span>
                            <span className="group-call-log-sub">
                              {initiatorName} · {subtitle}
                            </span>
                          </div>
                          <span className="group-call-log-time">{formatTime(msg.createdAt)}</span>
                        </div>
                      </div>
                    );
                  }
                  
                  return (
                    <div
                      key={msg._id || i}
                      data-message-id={msg._id}
                      className={`message ${isMe ? 'me' : 'them'}${isDeletedMessage ? ' deleted-message' : ''}`}
                      onDoubleClick={() => handleReplyToMessage(msg)}
                      onContextMenu={(e) => handleMessageContextMenu(e, msg)}
                      onTouchStart={(e) => {
                        // Handle long press on mobile
                        const timer = setTimeout(() => {
                          handleMessageContextMenu(e, msg);
                        }, 500);
                        
                        const handleTouchEnd = () => {
                          clearTimeout(timer);
                          document.removeEventListener('touchend', handleTouchEnd);
                        };
                        
                        document.addEventListener('touchend', handleTouchEnd);
                      }}
                    >
                      {/* Message sender name for group messages (kept for deleted messages too) */}
                      {!isMe && (
                        <div className="message-sender-name">
                          {msg.from?.username || 'User'}
                        </div>
                      )}

                      {/* Forwarded label */}
                      {msg.forwarded && !isDeletedMessage && (
                        <div className="forwarded-label">
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="15 17 20 12 15 7" />
                            <path d="M4 18v-2a4 4 0 0 1 4-4h12" />
                          </svg>
                          Forwarded
                        </div>
                      )}
                      
                      {/* Reply preview */}
                      {msg.replyTo && (
                        <div className="reply-preview-in-msg">
                          <span className="reply-author">
                            {msg.replyTo.from._id === user.id ? 'You' : (msg.replyTo.from.username || msg.replyTo.from.name || 'User')}
                          </span>
                          <span className="reply-text">
                            {msg.replyTo.messageType === 'image' ? '📷 Image' : 
                             msg.replyTo.messageType === 'video' ? '🎥 Video' : 
                             msg.replyTo.messageType === 'document' ? '📄 Document' :
                             msg.replyTo.messageType === 'audio' ? '🎵 Audio' : 
                             msg.replyTo.messageType === 'voice' ? '🎤 Voice message' :
                             msg.replyTo.text}
                          </span>
                        </div>
                      )}
                      
                      {/* Message content */}
                      {msg.messageType === 'voice' ? (
                        <VoiceMessage message={msg} isMe={isMe} />
                      ) : msg.viewOnce && (msg.messageType === 'image' || msg.messageType === 'video' || msg.messageType === 'audio') ? (
                        <ViewOnceMedia
                          message={msg}
                          currentUserId={user?.id}
                          isSender={isMe}
                          endpoint={`${config.apiUrl}/api/groups/messages/${msg._id}/view-once-media?userId=${user.id}`}
                          onViewed={(messageId) => {
                            setMessages((prev) =>
                              prev.map((item) =>
                                String(item._id) === String(messageId)
                                  ? {
                                      ...item,
                                      viewOnceViewed: true,
                                      viewedBy: [
                                        ...(item.viewedBy || []),
                                        { userId: user.id, viewedAt: new Date() },
                                      ],
                                    }
                                  : item
                              )
                            );
                          }}
                        />
                      ) : msg.messageType === 'image' ? (
                        <div className="msg-image">
                          <img 
                            src={getImageUrl(msg.imageUrl, config.apiUrl)} 
                            alt="Shared image" 
                            className="chat-image"
                            onClick={() => {
                              if (typeof onImageClick === 'function') {
                                onImageClick(msg.imageUrl);
                              }
                            }}
                          />
                        </div>
                      ) : msg.messageType === 'video' ? (
                        <div className="msg-video">
                          <video 
                            src={`${config.apiUrl}${msg.videoUrl}`} 
                            className="chat-video"
                            controls
                            preload="metadata"
                          >
                            Your browser does not support the video tag.
                          </video>
                        </div>
                      ) : msg.messageType === 'audio' ? (
                        <div className="msg-audio">
                          <div className="audio-info">
                            <div className="audio-icon">
                              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                <path d="M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3z" fill="#e74c3c"/>
                                <path d="M19 10v2a7 7 0 01-14 0v-2" stroke="#e74c3c" strokeWidth="2" fill="none"/>
                                <line x1="12" y1="19" x2="12" y2="23" stroke="#e74c3c" strokeWidth="2"/>
                                <line x1="8" y1="23" x2="16" y2="23" stroke="#e74c3c" strokeWidth="2"/>
                              </svg>
                            </div>
                            <div className="audio-details">
                              <span className="audio-name">{msg.audioName}</span>
                              <span className="audio-size">{msg.audioSize ? `${Math.round(msg.audioSize / 1024)} KB` : ''}</span>
                            </div>
                          </div>
                          <audio 
                            src={`${config.apiUrl}${msg.audioUrl}`} 
                            className="chat-audio"
                            controls
                            preload="metadata"
                          >
                            Your browser does not support the audio element.
                          </audio>
                        </div>
                      ) : msg.messageType === 'document' ? (
                        <div className="msg-document">
                          <div className="document-info">
                            <div className="document-icon">
                              {msg.documentType === 'PDF' ? (
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="#e74c3c">
                                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z"/>
                                  <path d="M14 2v6h6"/>
                                  <path d="M16 13H8"/>
                                  <path d="M16 17H8"/>
                                  <path d="M10 9H8"/>
                                </svg>
                              ) : (
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="#3498db">
                                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z"/>
                                  <path d="M14 2v6h6"/>
                                  <path d="M16 13H8"/>
                                  <path d="M16 17H8"/>
                                  <path d="M10 9H8"/>
                                </svg>
                              )}
                            </div>
                            <div className="document-details">
                              <span className="document-name">{msg.documentName}</span>
                              <span className="document-size">{msg.documentSize ? `${Math.round(msg.documentSize / 1024)} KB` : ''}</span>
                            </div>
                            <button 
                              onClick={() => {
                                // Download document
                                const link = document.createElement('a');
                                link.href = `${config.apiUrl}${msg.documentUrl}`;
                                link.download = msg.documentName;
                                link.click();
                              }}
                              className="document-download-btn"
                              type="button"
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
                                <polyline points="7,10 12,15 17,10"/>
                                <line x1="12" y1="15" x2="12" y2="3"/>
                              </svg>
                            </button>
                          </div>
                        </div>
                      ) : msg.messageType === 'contact' ? (
                        <ContactMessage 
                          contactData={msg.contactData}
                          currentUserId={user.id}
                          onContactClick={onContactClick}
                          isOwn={isMe}
                        />
                      ) : msg.messageType === 'location' ? (
                        <LocationMessage
                          locationData={msg.locationData}
                          isOwn={isMe}
                          timestamp={msg.createdAt}
                          senderName={isMe ? 'You' : msg.from.username || 'User'}
                          currentUserLocation={currentUserLocation}
                        />
                      ) : (
                        <div className={`msg-text notranslate${isDeletedMessage ? ' deleted' : ''}`}>
                          {isDeletedMessage ? msg.text : renderTextWithLinks(msg.text)}
                        </div>
                      )}
                      
                      {/* Message metadata */}
                      <span className="msg-meta">
                        {msg.isEdited && (
                          <span className="edited-indicator" title={`Edited at ${msg.editedAt ? new Date(msg.editedAt).toLocaleString() : ''}`}>
                            edited
                          </span>
                        )}
                        {formatTime(msg.createdAt)}
                        {/* Reactions summary */}
                        {msg.reactions && msg.reactions.length > 0 && (
                          <button
                            className="reactions-summary"
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              openReactionDetails(msg);
                            }}
                            title="See who reacted"
                          >
                            {(() => {
                              const counts = msg.reactions.reduce((acc, r) => {
                                acc[r.type] = (acc[r.type] || 0) + 1;
                                return acc;
                              }, {});
                              return Object.entries(counts).map(([emoji, count]) => (
                                <span key={emoji} className="reaction-chip">{emoji} {count}</span>
                              ));
                            })()}
                          </button>
                        )}
                        {pinnedMessageIds.includes(String(msg._id)) && (
                          <span className="pin-indicator" title="Pinned">📌</span>
                        )}
                        {msg.starredBy && msg.starredBy.includes(user.id) && (
                          <span className="star-indicator">
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="2">
                              <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26"/>
                            </svg>
                          </span>
                        )}
                      </span>
                    </div>
                  );
                })}
              </React.Fragment>
            ))}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>
      
      {/* Group Message Context Menu */}
      <MessageContextMenu
        message={{
          ...contextMenu.message,
          // Override isStarred to use starredBy array for group messages
          isStarred: contextMenu.message?.starredBy?.includes(user.id) || false,
          isPinned: pinnedMessageIds.includes(String(contextMenu.message?._id)),
          groupId: contextMenu.message?.groupId || group?._id,
        }}
        isVisible={contextMenu.isVisible}
        position={contextMenu.position}
        onClose={closeContextMenu}
        onDelete={handleDeleteMessage}
        onStar={handleStarMessage}
        onReply={handleReplyToMessage}
        onForward={handleForwardMessage}
        onReact={handleReactToMessage}
        onEdit={handleEditMessage}
        onViewStatus={handleViewMessageStatus}
        onPin={handlePinMessage}
        canPin={canPin && !pinnedMessageIds.includes(String(contextMenu.message?._id))}
        canUnpin={canPin && pinnedMessageIds.includes(String(contextMenu.message?._id))}
        isMe={contextMenu.message?.from?._id === user?.id}
        currentUser={user}
      />

      <ForwardPicker
        isVisible={forwardModal.isVisible}
        user={user}
        onClose={closeForwardModal}
        onForward={handleForwardConfirm}
      />

      {reactionDetailsMessage && (
        <div className="reaction-details-overlay" onClick={closeReactionDetails}>
          <div
            className="reaction-details-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="Message reactions"
          >
            <div className="reaction-details-header">
              <h4>Reactions</h4>
              <button
                type="button"
                className="reaction-details-close"
                onClick={closeReactionDetails}
                aria-label="Close reactions"
              >
                ×
              </button>
            </div>

            <div className="reaction-details-list">
              {(reactionDetailsMessage.reactions || []).map((reaction, index) => {
                const isOwnReaction =
                  getReactionUserId(reaction) === user?.id?.toString?.();
                return (
                  <div
                    className="reaction-details-item"
                    key={`${getReactionUserId(reaction) || 'unknown'}-${reaction.type}-${index}`}
                  >
                    <span className="reaction-details-emoji">{reaction.type}</span>
                    <span className="reaction-details-name">{getReactionUserDisplayName(reaction)}</span>
                    {isOwnReaction && (
                      <button
                        type="button"
                        className="reaction-details-remove"
                        onClick={() =>
                          handleRemoveOwnReaction(reactionDetailsMessage)
                        }
                      >
                        Remove
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default GroupMessages;
